import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Wallet, ShieldAlert, Activity, ShoppingCart, Coins, CheckCircle2,
  XCircle, Clock, Search, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

type ChargeStatus = 'all' | 'charged' | 'disputed' | 'refunded' | 'rejected';

export default function LeadMarketplaceConsole() {
  const qc = useQueryClient();

  // Pricing form
  const [city, setCity] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [price, setPrice] = useState('');

  // Activity filters
  const [status, setStatus] = useState<ChargeStatus>('all');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState('30');

  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - Number(days));
    return d.toISOString();
  }, [days]);

  // ── Pricing rules ───────────────────────────────────────────
  const { data: pricing } = useQuery({
    queryKey: ['lead-pricing'],
    queryFn: async () => {
      const { data } = await supabase
        .from('lead_pricing' as any)
        .select('*').order('created_at', { ascending: false });
      return (data as any[]) || [];
    },
  });

  // ── Activity: lead charges (filtered) ───────────────────────
  const { data: charges, isFetching: chargesLoading } = useQuery({
    queryKey: ['admin-lead-charges', status, since],
    queryFn: async () => {
      let q = supabase.from('lead_charges' as any)
        .select('*').gte('created_at', since)
        .order('created_at', { ascending: false }).limit(500);
      if (status !== 'all') q = q.eq('status', status);
      const { data } = await q;
      return (data as any[]) || [];
    },
  });

  // ── Activity: lead pack purchases (invoices for lead_wallet packages) ──
  const { data: purchases } = useQuery({
    queryKey: ['admin-lead-purchases', since],
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_invoices')
        .select('id, invoice_number, user_id, amount_etb, status, paid_at, created_at, package_id, advertising_packages!inner(name, category, sku)')
        .eq('advertising_packages.category', 'lead_wallet' as any)
        .gte('created_at', since)
        .order('created_at', { ascending: false }).limit(200);
      return (data as any[]) || [];
    },
  });

  // ── Wallet snapshots (top users) ────────────────────────────
  const { data: wallets } = useQuery({
    queryKey: ['admin-lead-wallets'],
    queryFn: async () => {
      const { data } = await supabase.from('lead_wallet' as any)
        .select('*').order('balance_etb', { ascending: false }).limit(20);
      return (data as any[]) || [];
    },
  });

  // ── Filter charges by user search ───────────────────────────
  const filteredCharges = useMemo(() => {
    if (!charges) return [];
    if (!search.trim()) return charges;
    const s = search.toLowerCase();
    return charges.filter((c) =>
      c.user_id?.toLowerCase().includes(s) ||
      c.lead_id?.toLowerCase().includes(s) ||
      c.reason?.toLowerCase().includes(s)
    );
  }, [charges, search]);

  const stats = useMemo(() => {
    const list = charges || [];
    return {
      total: list.length,
      charged: list.filter(c => c.status === 'charged').reduce((s, c) => s + Number(c.amount_etb || 0), 0),
      disputed: list.filter(c => c.status === 'disputed').length,
      refunded: list.filter(c => c.status === 'refunded').reduce((s, c) => s + Number(c.amount_etb || 0), 0),
      purchaseTotal: (purchases || []).filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount_etb || 0), 0),
    };
  }, [charges, purchases]);

  const addPrice = async () => {
    if (!price) return;
    await supabase.from('lead_pricing' as any).insert({
      city: city || null, property_type: propertyType || null, price_etb: Number(price),
    });
    toast.success('Pricing rule added');
    setCity(''); setPropertyType(''); setPrice('');
    qc.invalidateQueries({ queryKey: ['lead-pricing'] });
  };

  const togglePrice = async (id: string, isActive: boolean) => {
    await supabase.from('lead_pricing' as any).update({ is_active: !isActive }).eq('id', id);
    qc.invalidateQueries({ queryKey: ['lead-pricing'] });
  };

  const resolve = async (id: string, action: 'refund' | 'reject') => {
    const { error } = await supabase.rpc('resolve_lead_charge' as any, { _charge_id: id, _action: action });
    if (error) return toast.error(error.message);
    toast.success(action === 'refund' ? 'Refunded' : 'Dispute rejected');
    qc.invalidateQueries({ queryKey: ['admin-lead-charges'] });
  };

  const statusBadge = (s: string) => {
    const map: Record<string, { v: any; icon: any }> = {
      charged: { v: 'default', icon: CheckCircle2 },
      disputed: { v: 'secondary', icon: ShieldAlert },
      refunded: { v: 'outline', icon: RefreshCw },
      rejected: { v: 'destructive', icon: XCircle },
      paid: { v: 'default', icon: CheckCircle2 },
      pending: { v: 'secondary', icon: Clock },
    };
    const m = map[s] || { v: 'outline', icon: Clock };
    const Icon = m.icon;
    return <Badge variant={m.v} className="capitalize gap-1"><Icon className="h-3 w-3" />{s}</Badge>;
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wallet className="h-6 w-6 text-primary" /> Lead Marketplace
        </h1>
        <Button variant="outline" size="sm" onClick={() => {
          qc.invalidateQueries({ queryKey: ['admin-lead-charges'] });
          qc.invalidateQueries({ queryKey: ['admin-lead-purchases'] });
        }}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={Activity} label="Charges (range)" value={stats.total.toLocaleString()} />
        <KPI icon={Coins} label="Charged ETB" value={stats.charged.toLocaleString()} />
        <KPI icon={ShoppingCart} label="Pack revenue ETB" value={stats.purchaseTotal.toLocaleString()} />
        <KPI icon={ShieldAlert} label="Open disputes" value={stats.disputed.toLocaleString()} />
        <KPI icon={RefreshCw} label="Refunded ETB" value={stats.refunded.toLocaleString()} />
      </div>

      <Tabs defaultValue="activity" className="w-full">
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="disputes">Disputes</TabsTrigger>
          <TabsTrigger value="wallets">Wallets</TabsTrigger>
          <TabsTrigger value="pricing">Pricing rules</TabsTrigger>
        </TabsList>

        {/* ─── Activity ─── */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-xs text-muted-foreground">Search (user / lead / reason)</label>
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="user id, lead id..." className="pl-8" />
                  </div>
                </div>
                <div className="w-[160px]">
                  <label className="text-xs text-muted-foreground">Status</label>
                  <Select value={status} onValueChange={(v) => setStatus(v as ChargeStatus)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="charged">Charged</SelectItem>
                      <SelectItem value="disputed">Disputed</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[140px]">
                  <label className="text-xs text-muted-foreground">Date range</label>
                  <Select value={days} onValueChange={setDays}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last 12 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Lead charges</CardTitle></CardHeader>
            <CardContent className="p-0">
              {chargesLoading ? <p className="p-6 text-sm text-muted-foreground">Loading…</p>
                : filteredCharges.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No charges match your filters.</p>
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Lead</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCharges.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-xs whitespace-nowrap">{format(new Date(c.created_at), 'd MMM yy HH:mm')}</TableCell>
                          <TableCell className="font-mono text-xs">{c.user_id?.slice(0, 8)}</TableCell>
                          <TableCell className="font-mono text-xs">{c.lead_id?.slice(0, 8) || '—'}</TableCell>
                          <TableCell className="text-xs max-w-[240px] truncate">{c.reason || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(c.amount_etb).toLocaleString()} ETB</TableCell>
                          <TableCell>{statusBadge(c.status)}</TableCell>
                          <TableCell className="text-right">
                            {c.status === 'disputed' && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="outline" className="h-7" onClick={() => resolve(c.id, 'refund')}>Refund</Button>
                                <Button size="sm" variant="ghost" className="h-7" onClick={() => resolve(c.id, 'reject')}>Reject</Button>
                              </div>
                            )}
                            {c.status === 'refunded' && c.resolution_note && (
                              <span className="text-xs text-muted-foreground italic">{c.resolution_note}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Purchases ─── */}
        <TabsContent value="purchases">
          <Card>
            <CardHeader><CardTitle className="text-base">Lead pack purchases</CardTitle></CardHeader>
            <CardContent className="p-0">
              {!purchases?.length ? <p className="p-6 text-sm text-muted-foreground text-center">No purchases in this range.</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchases.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{p.invoice_number}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{format(new Date(p.created_at), 'd MMM yy')}</TableCell>
                        <TableCell className="font-mono text-xs">{p.user_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-sm">{p.advertising_packages?.name} <span className="text-xs text-muted-foreground">({p.advertising_packages?.sku})</span></TableCell>
                        <TableCell className="text-right tabular-nums">{Number(p.amount_etb).toLocaleString()} ETB</TableCell>
                        <TableCell>{statusBadge(p.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Disputes ─── */}
        <TabsContent value="disputes">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-500" /> Open disputes</CardTitle></CardHeader>
            <CardContent>
              {!(charges?.filter(c => c.status === 'disputed').length) ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No open disputes.</p>
              ) : (
                <div className="divide-y">
                  {charges!.filter(c => c.status === 'disputed').map((d) => (
                    <div key={d.id} className="py-3 flex items-center justify-between text-sm gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{Number(d.amount_etb).toLocaleString()} ETB</p>
                        <p className="text-xs text-muted-foreground">User {d.user_id?.slice(0, 8)} · {format(new Date(d.created_at), 'd MMM yyyy HH:mm')}</p>
                        <p className="text-xs text-muted-foreground italic mt-1">"{d.dispute_reason}"</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={() => resolve(d.id, 'refund')}>Refund</Button>
                        <Button size="sm" variant="outline" onClick={() => resolve(d.id, 'reject')}>Reject</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Wallets ─── */}
        <TabsContent value="wallets">
          <Card>
            <CardHeader><CardTitle className="text-base">Top wallet balances</CardTitle></CardHeader>
            <CardContent className="p-0">
              {!wallets?.length ? <p className="p-6 text-sm text-muted-foreground text-center">No wallets yet.</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Low alert</TableHead>
                      <TableHead>Auto-charge</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wallets.map((w) => (
                      <TableRow key={w.user_id}>
                        <TableCell className="font-mono text-xs">{w.user_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{Number(w.balance_etb).toLocaleString()} ETB</TableCell>
                        <TableCell className="text-right text-xs">{Number(w.low_balance_threshold).toLocaleString()}</TableCell>
                        <TableCell>{w.auto_charge ? <Badge variant="default">On</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
                        <TableCell className="text-xs">{format(new Date(w.updated_at), 'd MMM yy')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Pricing ─── */}
        <TabsContent value="pricing">
          <Card>
            <CardHeader><CardTitle className="text-base">Lead pricing rules</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 items-end">
                <Input placeholder="City (or blank for any)" value={city} onChange={(e) => setCity(e.target.value)} className="max-w-[200px]" />
                <Input placeholder="Property type (optional)" value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className="max-w-[200px]" />
                <Input placeholder="Price ETB" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="max-w-[140px]" />
                <Button onClick={addPrice}>Add rule</Button>
              </div>
              <div className="divide-y">
                {pricing?.map((p) => (
                  <div key={p.id} className="py-2 flex items-center justify-between text-sm">
                    <span>{p.city || 'Any city'} · {p.property_type || 'Any type'} → <strong>{Number(p.price_etb).toLocaleString()} ETB</strong></span>
                    <div className="flex items-center gap-2">
                      {!p.is_active && <Badge variant="outline">Disabled</Badge>}
                      <Button size="sm" variant="outline" onClick={() => togglePrice(p.id, p.is_active)}>
                        {p.is_active ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>
                ))}
                {!pricing?.length && <p className="text-sm text-muted-foreground py-6 text-center">No pricing rules yet.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <p className="text-xl font-bold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}
